/**
 * Shared render for a performance detail. Used by both:
 *  - Employee self-view (/pegawai/performa)
 *  - Admin viewing a specific employee (/admin/performa/:userId)
 *
 * Props:
 *  - data: response from /api/performance/me or /api/admin/performance/{user_id}
 */
import { Card } from "@/components/ui/card";
import { formatRupiah } from "@/lib/api";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, Cell } from "recharts";

const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const bln = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
  return `${bln[parseInt(m) - 1] || m} '${y.slice(2)}`;
};

const COLOR_BY_CAT = {
  green: { bg: "from-green-400 to-green-600" },
  blue: { bg: "from-blue-400 to-blue-600" },
  amber: { bg: "from-amber-400 to-amber-600" },
  red: { bg: "from-red-400 to-red-600" },
};

export const PerformanceView = ({ data, animatedScore }) => {
  const c = COLOR_BY_CAT[data.score.color] || COLOR_BY_CAT.blue;
  return (
    <>
      {/* GAUGE */}
      <Card className={`p-6 mb-4 bg-gradient-to-br ${c.bg} text-white border-0 shadow-lg`} data-testid="perf-gauge">
        <p className="overline text-white/80 text-center mb-2">Skor Performa Bulan Ini</p>
        <div className="relative w-48 h-48 mx-auto">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" />
            <circle
              cx="50" cy="50" r="42" fill="none" stroke="white" strokeWidth="8"
              strokeDasharray={`${(animatedScore / 100) * 263.9} 263.9`}
              strokeLinecap="round"
              style={{ transition: "stroke-dasharray 0.1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="font-display text-5xl font-bold tracking-tight">{Math.round(animatedScore)}</p>
            <p className="text-xs uppercase tracking-widest opacity-90">{data.score.category}</p>
          </div>
        </div>
        <p className="text-center text-sm mt-3 font-medium">{data.score.message}</p>
        {data.score.value < 70 && data.score.value > 0 && (
          <div className="mt-3 pt-3 border-t border-white/20 text-xs text-center bg-black/10 -mx-6 -mb-6 py-3 px-6 rounded-b-lg" data-testid="perf-kasbon-warning">
            ⚠️ Jika skor akhir bulan tetap di bawah 70, kasbon bulan depan akan terkunci.
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/20 text-center text-xs">
          <div>
            <p className="opacity-80">Kedisiplinan</p>
            <p className="font-bold text-base">{data.score.components.punctuality_pct}%</p>
            <p className="text-[0.6rem] opacity-75 mt-0.5 leading-tight">Datang ≤ {data.on_time_threshold || "08:10"}<br/>di Sen–Sab</p>
          </div>
          <div>
            <p className="opacity-80">Kehadiran</p>
            <p className="font-bold text-base">{data.score.components.attendance_pct}%</p>
            <p className="text-[0.6rem] opacity-75 mt-0.5 leading-tight">Hadir di hari<br/>kerja Sen–Sab</p>
          </div>
          <div>
            <p className="opacity-80">Bonus</p>
            <p className="font-bold text-base">+{data.score.components.bonus}</p>
            <p className="text-[0.6rem] opacity-75 mt-0.5 leading-tight">Reward jika<br/>0 hari telat</p>
          </div>
        </div>
      </Card>

      {/* ACHIEVEMENTS */}
      {data.achievements.length > 0 && (
        <Card className="p-4 mb-4 border border-zinc-200 shadow-none" data-testid="perf-achievements">
          <p className="overline mb-2">🏆 Pencapaian Bulan Ini</p>
          <div className="flex flex-wrap gap-2">
            {data.achievements.map((a) => (
              <div key={a.id} className="flex items-center gap-2 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300 rounded-md px-3 py-2 text-xs">
                <span className="text-lg">{a.icon}</span>
                <div>
                  <p className="font-bold text-amber-900">{a.name}</p>
                  <p className="text-amber-700 text-[0.65rem]">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <KpiMini label="Kehadiran" value={`${data.stats.days_present_workday}/${data.stats.workdays_expected}`} sub={data.stats.days_present_sunday > 0 ? `+${data.stats.days_present_sunday} hari Minggu` : "hari kerja"} tid="kpi-hadir" />
        <KpiMini label="Hari Telat" value={data.stats.days_late} sub={`${data.stats.late_min} menit`} tid="kpi-telat" />
        <KpiMini label="Lembur" value={`${Math.floor(data.stats.overtime_min / 60)}j ${data.stats.overtime_min % 60}m`} sub={formatRupiah(data.stats.overtime_wage)} tid="kpi-lembur" />
        <KpiMini label="Streak Tanpa Telat" value={`${data.stats.streak_no_late} hari`} sub="berturut" tid="kpi-streak" />
      </div>

      {/* INSIGHTS */}
      {data.insights.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="perf-insights">
          {data.insights.map((i, k) => {
            const Icon = i.type === "success" ? CheckCircle2 : i.type === "warning" ? AlertCircle : Info;
            const cls = i.type === "success" ? "bg-green-50 border-green-300 text-green-800"
              : i.type === "warning" ? "bg-amber-50 border-amber-300 text-amber-800"
              : "bg-blue-50 border-blue-300 text-blue-800";
            return (
              <div key={k} className={`flex items-start gap-2 p-3 rounded-md border text-xs ${cls}`}>
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="leading-relaxed">{i.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* TREND 6 BULAN */}
      <Card className="p-4 mb-4 border border-zinc-200 shadow-none" data-testid="perf-trend">
        <p className="overline mb-1">📈 Tren 6 Bulan</p>
        <h3 className="font-display text-sm font-semibold mb-2">Ketepatan Waktu (%) per bulan</h3>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <LineChart data={data.trend.map(t => ({ ...t, label: fmtMonth(t.period) }))} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="punctuality" name="Ketepatan Waktu %" stroke="#0055FF" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* STATISTIK KEDISIPLINAN */}
      <Card className="p-4 mb-4 border border-zinc-200 shadow-none" data-testid="perf-stats">
        <p className="overline mb-2">⏱ Statistik Kedisiplinan</p>
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div className="bg-zinc-50 rounded p-2">
            <p className="text-xs text-zinc-500">Jam datang rata-rata</p>
            <p className="font-mono font-bold text-base">{data.stats.avg_arrival || "—"} WIB</p>
          </div>
          <div className="bg-zinc-50 rounded p-2">
            <p className="text-xs text-zinc-500">Jam pulang rata-rata</p>
            <p className="font-mono font-bold text-base">{data.stats.avg_departure || "—"} WIB</p>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mb-2 mt-3">Distribusi waktu datang bulan ini:</p>
        <div style={{ width: "100%", height: 160 }}>
          <ResponsiveContainer>
            <BarChart data={data.arrival_distribution} margin={{ top: 5, right: 5, bottom: 30, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="bucket" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {data.arrival_distribution.map((entry, i) => {
                  const isOnTime = entry.bucket === "08:00-08:10";
                  const isLate = entry.bucket.startsWith("08:30") || entry.bucket.startsWith(">09");
                  const color = isLate ? "#dc2626" : isOnTime ? "#16a34a" : "#0055FF";
                  return <Cell key={i} fill={color} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {(data.weekday_summary.worst || data.weekday_summary.best) && (
          <div className="mt-3 pt-3 border-t border-zinc-200 text-xs space-y-1">
            {data.weekday_summary.worst && (
              <p>🔴 Paling rawan telat: <b>{data.weekday_summary.worst.day}</b> ({data.weekday_summary.worst.late}x)</p>
            )}
            {data.weekday_summary.best && (
              <p>🟢 Paling konsisten: <b>{data.weekday_summary.best.day}</b></p>
            )}
          </div>
        )}
      </Card>

      {/* FINANSIAL */}
      {data.pay_type === "monthly" && (
        <Card className="p-4 mb-4 border border-zinc-200 shadow-none" data-testid="perf-finansial">
          <p className="overline mb-1">💰 Ringkasan Finansial</p>
          <h3 className="font-display text-sm font-semibold mb-2">Estimasi Take Home Bulan Ini</h3>
          <p className="font-display text-2xl font-bold tracking-tight text-primary mb-3">{formatRupiah(data.financial.thp_estimate)}</p>
          <div className="space-y-1 text-xs">
            <Row label="Gaji Pokok" value={formatRupiah(data.financial.salary)} positive />
            <Row label="Lembur" value={formatRupiah(data.financial.overtime_wage)} positive />
            <Row label="Potongan Telat" value={`(${formatRupiah(data.financial.deduction)})`} negative />
            <Row label="Kasbon" value={`(${formatRupiah(data.financial.kasbon_used)})`} negative />
            <Row label="Cicilan Pinjaman" value={`(${formatRupiah(data.financial.loan_deduction)})`} negative />
          </div>
          {data.financial.kasbon_limit > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-200">
              <p className="text-xs text-zinc-500 mb-1">Kuota Kasbon Bulan Ini</p>
              <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full ${data.financial.kasbon_used / data.financial.kasbon_limit > 0.8 ? "bg-red-500" : "bg-primary"}`}
                  style={{ width: `${Math.min(100, data.financial.kasbon_used / data.financial.kasbon_limit * 100)}%` }}
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                {formatRupiah(data.financial.kasbon_used)} terpakai dari {formatRupiah(data.financial.kasbon_limit)}
              </p>
            </div>
          )}
          {data.financial.loan_outstanding > 0 && (
            <div className="mt-3 pt-3 border-t border-zinc-200 text-xs">
              <p className="text-zinc-500">Total Pinjaman Aktif</p>
              <p className="font-mono font-bold text-amber-700">{formatRupiah(data.financial.loan_outstanding)}</p>
            </div>
          )}
        </Card>
      )}

      {/* VS TIM */}
      {data.team_comparison && (
        <Card className="p-4 mb-4 border border-zinc-200 shadow-none" data-testid="perf-vs-team">
          <p className="overline mb-1">📊 Anda vs Tim</p>
          <h3 className="font-display text-sm font-semibold mb-3">Bulan ini (rata-rata tim)</h3>
          <CompareBar
            label="Ketepatan Waktu"
            mine={data.team_comparison.my_punctuality}
            team={data.team_comparison.team_punctuality}
            unit="%"
            biggerIsBetter
          />
          <CompareBar
            label="Hari Telat"
            mine={data.team_comparison.my_late_days}
            team={data.team_comparison.team_late_days_avg}
            unit=" hari"
            biggerIsBetter={false}
          />
        </Card>
      )}
    </>
  );
};

const KpiMini = ({ label, value, sub, tid }) => (
  <Card className="p-3 border border-zinc-200 shadow-none" data-testid={tid}>
    <p className="overline text-[0.6rem] mb-1">{label}</p>
    <p className="font-display text-base sm:text-lg font-bold tracking-tight">{value}</p>
    {sub && <p className="text-[0.65rem] text-zinc-500 truncate">{sub}</p>}
  </Card>
);

const Row = ({ label, value, positive, negative }) => (
  <div className="flex justify-between">
    <span className="text-zinc-600">{label}</span>
    <span className={`font-mono ${positive ? "text-green-700" : negative ? "text-red-700" : ""}`}>{value}</span>
  </div>
);

const CompareBar = ({ label, mine, team, unit, biggerIsBetter }) => {
  const better = biggerIsBetter ? mine > team : mine < team;
  const max = Math.max(mine, team, 1);
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-zinc-600">{label}</span>
        <span className={better ? "text-green-700 font-medium" : "text-zinc-500"}>
          {better ? "🎉 Lebih baik" : "Bisa ditingkatkan"}
        </span>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[0.65rem] w-12 text-zinc-500">Anda</span>
          <div className="flex-1 bg-zinc-100 rounded-full h-3 overflow-hidden">
            <div className={`h-full ${better ? "bg-green-500" : "bg-primary"}`} style={{ width: `${(mine / max) * 100}%` }} />
          </div>
          <span className="text-xs font-mono w-12 text-right">{mine}{unit}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[0.65rem] w-12 text-zinc-500">Tim</span>
          <div className="flex-1 bg-zinc-100 rounded-full h-3 overflow-hidden">
            <div className="h-full bg-zinc-400" style={{ width: `${(team / max) * 100}%` }} />
          </div>
          <span className="text-xs font-mono w-12 text-right">{team}{unit}</span>
        </div>
      </div>
    </div>
  );
};
